import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { DocumentPreview } from '@/components/atoms/DocumentPreview/DocumentPreview';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import useDocuments from '@/hooks/useDocuments';
import { downloadDocument } from '@/modules/collections/helper';
import { popModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';

type Props = { document_id: string; datasource_id: string; collection_id: string };

/**
 * Modal component for previewing documents with download functionality
 * @param document_id - The ID of the document to preview
 * @param datasource_id - The ID of the datasource containing the document
 * @param collection_id - The ID of the collection containing the document
 */
function ModalDocumentPreview({ document_id, datasource_id, collection_id }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(true);

  const slug = useSelector(selectAccountSlug);

  const { document, documentPreview, fetchPreviewUrl, getStatus } = useDocuments(
    collection_id,
    datasource_id,
    document_id
  );

  // Fetch preview URL when component mounts
  useEffect(() => {
    if (collection_id && datasource_id && document_id) {
      fetchPreviewUrl();
    }
  }, [collection_id, datasource_id, document_id, fetchPreviewUrl]);

  const handleDownload = () => {
    downloadDocument(document, collection_id, slug);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      dispatch(popModal());
    }
  };

  const isLoading = getStatus === 'pending';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t('common.preview')}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-x-hidden overflow-y-auto">
          <DocumentPreview url={documentPreview?.preview_url} document={document} />
        </div>

        <DialogFooter className="shrink-0 sm:justify-center">
          <Button onClick={handleDownload} disabled={isLoading}>
            {t('collections.download')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ModalDocumentPreview;
